import AlertPanel from 'ext-ui/components/AlertPanel/AlertPanel'
import BaseModal from 'ext-ui/components/BaseModal/BaseModal'
import Button from 'ext-ui/components/Button/Button'
import TextInput from 'ext-ui/components/TextInput/TextInput'
import React, { FormEvent, useCallback, useEffect, useState } from 'react'
import { useTranslate } from 'src/localization/Trns'
import { toCents } from 'src/utils/currency.utils'

import style from './FundingAccountVerificationModal.style'

type AmountFields = {
  first: string
  second: string
}

type FieldErrors = Partial<Record<keyof AmountFields, string>>

export interface FundingAccountVerificationModalProps {
  open: boolean
  submitting: boolean
  serverError?: string | null
  onClose: () => void
  onSubmit: (amounts: [number, number]) => Promise<void>
}

const initialAmounts: AmountFields = { first: '', second: '' }

/** Upper bound is the API's; zero is rejected here or it reaches the wire. */
const parseDollarAmount = (value: string): number | null => {
  const normalized = value.trim()
  if (!/^(?:\d+(?:\.\d{0,2})?|\.\d{1,2})$/.test(normalized)) return null
  const dollars = Number(normalized)
  if (!Number.isFinite(dollars)) return null
  const cents = toCents(dollars)
  // A regex-valid but enormous entry overflows to Infinity, which serializes
  // as JSON null and reaches the API as a malformed amount.
  return Number.isSafeInteger(cents) && cents > 0 ? cents : null
}

const FundingAccountVerificationModal: React.FC<
  FundingAccountVerificationModalProps
> = ({ open, submitting, serverError, onClose, onSubmit }) => {
  const translate = useTranslate()
  const [amounts, setAmounts] = useState<AmountFields>(initialAmounts)
  const [errors, setErrors] = useState<FieldErrors>({})
  const clear = useCallback((): void => {
    setAmounts(initialAmounts)
    setErrors({})
  }, [])

  useEffect(() => {
    if (!open) clear()
  }, [clear, open])

  const validate = (): [number, number] | null => {
    const first = parseDollarAmount(amounts.first)
    const second = parseDollarAmount(amounts.second)
    const nextErrors: FieldErrors = {}

    if (first == null) {
      nextErrors.first = amounts.first
        ? translate(
            'Enter a numerical dollar amount with no more than two decimal places.',
          )
        : translate('Enter the first deposit amount.')
    }

    if (second == null) {
      nextErrors.second = amounts.second
        ? translate(
            'Enter a numerical dollar amount with no more than two decimal places.',
          )
        : translate('Enter the second deposit amount.')
    }

    setErrors(nextErrors)
    return first == null || second == null ? null : [first, second]
  }

  const handleClose = (): void => {
    clear()
    onClose()
  }

  const handleSubmit = async (
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault()
    const parsedAmounts = validate()
    if (!parsedAmounts) return

    try {
      await onSubmit(parsedAmounts)
    } catch {
      // The page owns safe server-error copy. Amount fields are still cleared in
      // finally so submitted amounts are not retained after a provider response.
    } finally {
      clear()
    }
  }

  return (
    <BaseModal
      isOpen={open}
      onClose={submitting ? (): void => undefined : handleClose}
      showCloseButton={!submitting}
      shouldCloseOnEsc={!submitting}
      shouldCloseOnOverlayClick={!submitting}
      variant="interactive"
      aria-label={translate('Verify your bank account')}
    >
      <div className="FundingAccountVerificationModal" css={style}>
        <div className="FundingAccountVerificationModal-header">
          <h2 className="FundingAccountVerificationModal-title">
            {translate('Verify your bank account')}
          </h2>
          <p className="FundingAccountVerificationModal-copy">
            {translate(
              'Two deposits normally arrive in 1-2 business days. Enter the dollar amount of each deposit to verify this account.',
            )}
          </p>
        </div>

        <form
          className="FundingAccountVerificationModal-form"
          onSubmit={handleSubmit}
        >
          {serverError && (
            <AlertPanel variant="severe">{serverError}</AlertPanel>
          )}
          <div className="FundingAccountVerificationModal-fields">
            <TextInput
              label={translate('First deposit amount (USD)')}
              requirement="required"
              inputMode="decimal"
              autoComplete="off"
              value={amounts.first}
              iconLeft={<span aria-hidden="true">{'$'}</span>}
              onChange={(event): void =>
                setAmounts((current) => ({
                  ...current,
                  first: event.target.value,
                }))
              }
              disabled={submitting}
              errorText={errors.first}
              testId="funding-verification-first-amount"
            />
            <TextInput
              label={translate('Second deposit amount (USD)')}
              requirement="required"
              inputMode="decimal"
              autoComplete="off"
              value={amounts.second}
              iconLeft={<span aria-hidden="true">{'$'}</span>}
              onChange={(event): void =>
                setAmounts((current) => ({
                  ...current,
                  second: event.target.value,
                }))
              }
              disabled={submitting}
              errorText={errors.second}
              testId="funding-verification-second-amount"
            />
          </div>
          <div className="FundingAccountVerificationModal-footer">
            <Button
              variant="secondary"
              onClick={handleClose}
              disabled={submitting}
              testId="funding-verification-cancel"
            >
              {translate('Cancel')}
            </Button>
            <Button
              type="submit"
              loading={submitting}
              testId="funding-verification-submit"
            >
              {translate('Verify deposits')}
            </Button>
          </div>
        </form>
      </div>
    </BaseModal>
  )
}

export default FundingAccountVerificationModal
